
import {
  computed, defineComponent, inject, PropType, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import {
  ICase, IFolder, IGroup, IMedia, INotification,
} from '@/types/interfaces';
import { useAlertsStore } from '../store/alertsStore';
import { useMainStore } from '@/store/mainStore';
import {
  MEDIA_TYPES, NOTIFICATION_TYPE, NOTIFICATION_STATUS, PHOTO_VIEW_TYPE,
} from '@/types/enums';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import ItemPhoto from '@/modules/drive/components/items/ItemPhoto.vue';
import ItemFile from '@/modules/drive/components/items/ItemFile.vue';
import ItemFolder from '@/modules/drive/components/items/ItemFolder.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';

export default defineComponent({
  components: {
    ItemCase,
    ItemPhoto,
    ItemFile,
    ItemFolder,
  },
  props: {
    notification: { type: Object as PropType<INotification>, default: {} as INotification, required: true },
  },
  setup(props) {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localizeIfPresent = inject('$localizeIfPresent') as Function;
    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const alertStore = useAlertsStore();

    const unread = ref(!props.notification.read);

    const isGroup = computed(() => (
      props.notification.type === NOTIFICATION_TYPE.GROUP_REQUEST_SENT
   || props.notification.type === NOTIFICATION_TYPE.GROUP_REQUEST_ACCEPT
   || props.notification.type === NOTIFICATION_TYPE.GROUP_REQUEST_REJECT
   || props.notification.type === NOTIFICATION_TYPE.IN_GROUP_REQUEST_ACCEPT
   || props.notification.type === NOTIFICATION_TYPE.IN_GROUP_REQUEST_REJECT
   || props.notification.type === NOTIFICATION_TYPE.REMOVED_FROM_GROUP
   || props.notification.type === NOTIFICATION_TYPE.USER_LEFT_THE_GROUP
    ));

    const photoUrl = computed(() => {
      if (isGroup.value) {
        if ((props.notification.sharedInfo as IGroup)?.photo?.url) {
          return lowResUrlByViewTypeByUrl((props.notification.sharedInfo as IGroup)?.photo?.url, PHOTO_VIEW_TYPE.WHSML);
        }
        // eslint-disable-next-line global-require
        return require('@/assets/img/mockup/Group.svg');
      }
      if (props.notification.partner.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.notification.partner.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    const fullName = computed(() => `${props.notification.partner.firstName} ${props.notification.partner.lastName}`);

    const alertTitle = computed(() => {
      if (isGroup.value) {
        return props.notification.content;
      }
      return fullName.value;
    });

    const alertText = computed(() => {
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_CASE) {
        return `${$localize('shared.the.case')} "${(props.notification.content)}" ${$localize('with.you')}`;
      }
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_FILE) {
        if (!props.notification.sharedInfo) {
          return `${$localize('shared.the.media')} "${(props.notification.content)}" ${$localize('with.you')}`;
        }
        return `shared the ${(props.notification.sharedInfo as IMedia).type === MEDIA_TYPES.PHOTO ? 'photo' : 'file'} ${$localize('with.you')}`;
      }
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_FOLDER) {
        if (!props.notification.sharedInfo) {
          return `${$localize('shared.the.folder')} "${(props.notification.content)}" ${$localize('with.you')}`;
        }
        return `${$localize('shared.the.folder')} "${(props.notification.sharedInfo as IFolder).name}" ${$localize('with.you')}`;
      }
      if (props.notification.type === NOTIFICATION_TYPE.IN_GROUP_REQUEST_SENT) {
        if (!props.notification.status) {
          return `${$localize('group.request.to.join')} "${(props.notification?.sharedInfo as IGroup)?.name}"`;
        }
        if (props.notification.status === NOTIFICATION_STATUS.ACCEPTED) {
          return `${$localize('group.request.to.join.accepted')} "${(props.notification?.sharedInfo as IGroup)?.name}"`;
        }
        if (props.notification.status === NOTIFICATION_STATUS.REJECTED) {
          return `${$localize('group.request.to.join.rejected')} "${(props.notification?.sharedInfo as IGroup)?.name}"`;
        }
      }
      return '';
    });

    const currentDate = (new Date()).getTime();

    const sharedCase = computed(() => {
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_CASE && props.notification.sharedInfo) {
        return props.notification.sharedInfo as ICase;
      }
      return null;
    });

    const sharedMedia = computed(() => {
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_FILE) {
        return props.notification.sharedInfo as IMedia;
      }
      return null;
    });

    const sharedFolder = computed(() => {
      if (props.notification.type === NOTIFICATION_TYPE.SHARE_FOLDER) {
        return props.notification.sharedInfo as IFolder;
      }
      return null;
    });

    const classObject = computed(() => ({
      'background-none': props.notification.type === NOTIFICATION_TYPE.CONTACT_REQUEST_ACCEPT
                         || props.notification.type === NOTIFICATION_TYPE.CONTACT_REQUEST_REJECT,
    }));

    const date = computed(() => {
      const minutes = Math.abs(currentDate - props.notification.creationDate) / (1000 * 60);
      if (minutes >= 24 * 60) {
        return `${Math.floor(minutes / (24 * 60))}d ago`;
      }
      if (minutes >= 60) {
        return `${Math.floor(minutes / 60)}h ago`;
      }
      return `${Math.floor(minutes)}m ago`;
    });

    function manageContact(contactId: string, accept = false) {
      alertStore.manageContact(contactId, accept);
    }

    function manageGroup(groupId: string, partnerId: string, accept = false) {
      alertStore.manageGroup(groupId, partnerId, accept);
    }

    function manageRequestToGroup(groupId: string, partnerId: string, accept = false) {
      alertStore.manageRequestToGroup(groupId, partnerId, accept);
    }

    function viewProfile() {
      router.push(`/profile/${props.notification.partnerId.internalId}`);
    }

    function viewCase() {
      router.push(`/drive/case/network/${(props.notification.sharedInfo as ICase).id}/view/`);
    }

    function viewPhoto(photo: IMedia) {
      driveStore.fullscreenPhoto = photo;
    }

    function viewFile(file: IMedia) {
      if (mainStore.isMobile) {
        driveStore.fullscreenFile = file;
      }
      if (file.name.includes('.pdf') && !mainStore.isMobile) {
        const fileUrl = (process.env.VUE_APP_MEDIA_URL || '') + file.fileUrl;
        window.open(fileUrl, '_blank');
      }
    }

    function viewFolder() {
      router.push(`/drive/network/folder/${sharedFolder.value?.id}`);
    }

    return {
      photoUrl,
      fullName,
      alertTitle,
      alertText,
      date,
      classObject,
      sharedCase,
      sharedMedia,
      sharedFolder,
      manageContact,
      manageGroup,
      manageRequestToGroup,
      viewProfile,
      viewCase,
      viewPhoto,
      viewFile,
      viewFolder,
      NOTIFICATION_TYPE,
      NOTIFICATION_STATUS,
      MEDIA_TYPES,
      unread,
      mainStore,
    };
  },
});
